import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Sparkles, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, getStudentId } from "@/lib/api";
import { whenAudioIdle } from "@/lib/audioLock";
import { useVoicePrompt } from "@/hooks/use-voice-prompt";
import VoicePromptBanner from "@/components/VoicePromptBanner";
import { randomCorrectPhrase, randomIncorrectPhrase, withRandomIntro } from "@/lib/questionPhrases";

const AUTO_ADVANCE_DELAY_MS = 2800;

function isDiagnosticCorrect(given, correctOption) {
  const g = (given || "").trim();
  const c = (correctOption || "").trim();
  const givenLetter = g.includes(")") ? g.split(")")[0].trim() : g;
  return givenLetter === c || g === c;
}

export default function DiagnosticQuiz({ conceptId, onComplete }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedOption, setSelectedOption] = useState(null);
  const [checkResult, setCheckResult] = useState(null);
  const [result, setResult] = useState(null);
  const studentId = getStudentId();

  const currentQuestion = questions[step];
  const questionVoiceText = useMemo(
    () => (currentQuestion ? withRandomIntro(currentQuestion.text) : ""),
    [currentQuestion?.id, step]
  );
  const { blocked: voiceBlocked, retry: retryVoice } = useVoicePrompt(
    questionVoiceText,
    currentQuestion ? `diagnostic-${conceptId}-q${step}` : null,
    { enabled: !!currentQuestion }
  );
  const feedbackVoiceText = useMemo(
    () => (checkResult ? (checkResult.correct ? randomCorrectPhrase() : randomIncorrectPhrase()) : ""),
    [checkResult] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const { blocked: feedbackVoiceBlocked, retry: retryFeedbackVoice } = useVoicePrompt(
    feedbackVoiceText,
    checkResult ? `diagnostic-feedback-${conceptId}-q${step}` : null,
    { enabled: !!checkResult }
  );

  useEffect(() => {
    api.post("/tutor/diagnostic/generate", { student_id: studentId, concept_id: conceptId })
      .then(({ data }) => setQuestions(data.questions || []))
      .finally(() => setLoading(false));
  }, [conceptId, studentId]);

  const submit = async (finalAnswers) => {
    const { data } = await api.post("/tutor/diagnostic/submit", {
      student_id: studentId, concept_id: conceptId, answers: finalAnswers, questions,
    });
    setResult(data);
  };

  const goNext = () => {
    const q = questions[step];
    const nextAnswers = { ...answers, [q.id]: selectedOption };
    setAnswers(nextAnswers);
    setCheckResult(null);
    setSelectedOption(null);
    if (step + 1 < questions.length) setStep((s) => s + 1);
    else submit(nextAnswers);
  };

  useEffect(() => {
    if (!checkResult) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      await whenAudioIdle();
      if (!cancelled) goNext();
    }, AUTO_ADVANCE_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkResult]);

  const handleAnswer = (option) => {
    if (checkResult) return;
    const q = questions[step];
    setSelectedOption(option);
    setCheckResult({ correct: isDiagnosticCorrect(option, q.correct_option) });
  };

  if (loading) {
    return <div className="flex flex-col items-center justify-center h-64 text-center" data-testid="diagnostic-loading">
      <Sparkles className="text-primary animate-pulse mb-3" size={28} />
      <p className="text-sm text-muted-foreground">Checking what you already know...</p>
    </div>;
  }

  if (result) {
    return (
      <div className="text-center py-8 animate-float-in" data-testid="diagnostic-result">
        <p className="text-lg font-heading font-bold mb-2">{result.message}</p>
        <p className="text-sm text-muted-foreground mb-6">Readiness score: {result.score}%</p>
        <Button data-testid="diagnostic-continue-button" onClick={() => onComplete(result)} className="rounded-xl px-8 h-11">
          Continue to Lesson
        </Button>
      </div>
    );
  }

  if (!questions.length) {
    return <div className="py-8"><Button data-testid="diagnostic-skip-button" onClick={() => onComplete({ ready: true })}>Start Lesson</Button></div>;
  }

  const q = questions[step];
  return (
    <div className="py-4" data-testid="diagnostic-quiz">
      <VoicePromptBanner
        blocked={voiceBlocked || feedbackVoiceBlocked}
        onRetry={() => { retryVoice(); retryFeedbackVoice(); }}
        testId="diagnostic-voice-unlock-banner"
      />
      <div className="flex gap-1.5 mb-6">
        {questions.map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-muted"}`} />
        ))}
      </div>
      <p className="text-xs text-muted-foreground mb-2">Quick check {step + 1} of {questions.length}</p>
      <p className="text-lg font-semibold mb-5" data-testid="diagnostic-question-text">{q.text}</p>
      <div className="space-y-2">
        {q.options?.map((opt) => {
          const isSelected = selectedOption === opt;
          const showCorrect = checkResult && isSelected && checkResult.correct;
          const showIncorrect = checkResult && isSelected && !checkResult.correct;
          return (
            <button
              key={opt}
              data-testid={`diagnostic-option-${opt.replace(/\s+/g, '-')}`}
              onClick={() => handleAnswer(opt)}
              disabled={!!checkResult}
              className={`w-full text-left px-4 py-3 rounded-xl border transition-colors text-sm font-medium disabled:opacity-70 ${
                showCorrect ? "border-success bg-success/10" : showIncorrect ? "border-destructive bg-destructive/10" :
                isSelected ? "border-primary bg-primary/10" : "border-border hover:border-primary hover:bg-primary/5"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
      {checkResult && (
        <div className="mt-5 animate-float-in" data-testid="diagnostic-feedback">
          <div className={`flex items-center gap-2 font-semibold ${checkResult.correct ? "text-success" : "text-destructive"}`}>
            {checkResult.correct ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
            {checkResult.correct ? "Correct!" : "Not quite"}
          </div>
          <Button variant="ghost" size="sm" data-testid="diagnostic-skip-pause-button" onClick={goNext} className="mt-3 text-xs">
            Continue now →
          </Button>
        </div>
      )}
    </div>
  );
}
